/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '~/store/config';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import { _, ToastMessage } from '~/dependencies';
import { useNavigation, useConversation } from '~/hooks';
import { ChannelTypeEnum } from '~/enums/conversations';
import { DraftMessage } from '~/interfaces/conversations';
import {
  ContactOrGroup,
  Contact,
  ContactGroup,
  SmsTemplate,
  SmsSender,
} from '~/types/accountInfo';
import { log } from '~/helpers';
import {
  isContact,
  isContactGroup,
} from '../components/RecipientsSelector/utils';
import RecipientsSelector from '../components/RecipientsSelector';
import SenderSelector from '../components/SenderSelector';
import MessageTextInput from '../components/MessageTextInput';
import ConversationWarnings from '../components/ConversationWarnings';
import ChannelSelector from '../components/ChannelSelector';
import {
  Container,
  Right,
  Block,
  MessageHeader,
  MessageContent,
  CancelButton,
} from './styles';

/**
 * The maximum number of recipients that can be selected.
 */
const MAX_RECIPIENTS = 10;

export default function New(props: any) {
  // HOOK'S
  const converstionFilters = useSelector(
    (state: RootState) => state.conversation.filters
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    loading,
    conversationId,
    submitted,
    setSubmitted,
    errorMessage,
    ...conversation
  } = useConversation();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state: {
      id?: number;
      name?: string | null;
      mobile: string;
    };
  };

  // STATE'S
  const [recipientsList, setRecipientsList] = useState([]);
  const [invalidRecipientsList, setInvalidRecipientsList] = useState([]);
  const [template, setTemplate] = useState<SmsTemplate | null>(null);
  const [sender, setSender] = useState<SmsSender | null>(null);
  const [channelType, setChannelType] = useState(
    converstionFilters?.channelType || ChannelTypeEnum.SMS
  );

  // CONST'S
  const hasInvalidRecipients = invalidRecipientsList.length > 0;
  // WhatsApp requires an approved template to start a conversation.
  // Other channels (SMS) accept either a sender or a template.
  const isMissingRequiredSenderOrTemplate =
    channelType === ChannelTypeEnum.WHATSAPP
      ? template === null
      : sender === null && template === null;

  const isSendMessageBtnDisabled =
    _.isEmpty(recipientsList) ||
    hasInvalidRecipients ||
    isMissingRequiredSenderOrTemplate;

  const hasNoReceipientsOrSender = _.isEmpty(recipientsList) || sender === null;

  const initialRecipientsList = location.state?.mobile
    ? [
        {
          id: location.state.id ?? 0, // Ensure id is a number, fallback to 0 if undefined
          name: location.state.name ?? '', // Ensure name is always a string
          mobile: location.state.mobile,
        },
      ]
    : [];

  // EFFECT'S

  useEffect(() => {
    if (converstionFilters?.channelType) {
      setChannelType(converstionFilters.channelType);
    }
  }, [converstionFilters, setChannelType]);

  useEffect(() => {
    dispatch(actions.fetchMessageSenders());
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.setHiddenBottomNav(true));

    return () => {
      dispatch(actions.setHiddenBottomNav(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!loading && submitted) {
      if (errorMessage) {
        ToastMessage('error', errorMessage);
        setSubmitted(false);
      } else if (conversationId) {
        navigate.chatView({ id: conversationId });
      } else if (recipientsList.length > 1) {
        // If multiple recipients, show success message and navigate back to conversations list
        ToastMessage('success', 'Messages sent successfully');
        setTimeout(() => navigate.conversations(), 300);
      } else {
        navigate.conversations();
      }
    }
  }, [
    loading,
    submitted,
    setSubmitted,
    conversationId,
    errorMessage,
    channelType,
    recipientsList,
    sender,
    navigate,
  ]);

  const handleSendMessage = async (draftMessage: DraftMessage) => {
    try {
      // The list of phone number to receive the message e.g, "+3530000000,+35311111111"
      const contacts: string = recipientsList
        .filter((participant: ContactOrGroup) => isContact(participant))
        // @ts-ignore
        .map(({ mobile }: Contact) => mobile)
        .join(',');

      // The list of group IDs to receive the message e.g, "1,2,3"
      const groups: string = recipientsList
        .filter((participant: ContactOrGroup) => isContactGroup(participant))
        // @ts-ignore
        .map(({ groupId }: ContactGroup) => groupId)
        .join(',');

      const templateId = template?.id || draftMessage?.templateId;
      const senderName = sender?.name;
      // const senderId = templateId ? undefined : senderName;
      const senderId = senderName;

      const payload = {
        channelType: channelType as ChannelTypeEnum,
        contacts,
        groups,
        senderId,
        senderName,
        templateId,
        message: draftMessage.text,
        templateParams: draftMessage?.templateParams,
      };

      await conversation.create(payload);
    } catch (err) {
      log.error('Failed to create conversation. Reason: ', err);
    }
  };

  /**
   * When a template is appended to the message input, set its senders as default selected senders
   */
  const handleOnTemplateAppend = (smsTemplate: SmsTemplate) => {
    setTemplate(smsTemplate);
    // For 10DLC, sender comes from template itself, user cannot select sender
    if (smsTemplate.usage === '10dlc') {
      setSender(null);
    }
  };

  const handleOnTemplateRemoved = () => {
    setSender(null);
    setTemplate(null);
  };

  const handleChannelChange = (channelType: ChannelTypeEnum) => {
    dispatch(actions.setConversationFilter({ channelType }));
    dispatch(actions.fetchMessageSenders());
    setSender(null);
    setTemplate(null);
  };

  const componentInfoLogger = () => {
    log.debug('New Conversations Info: ', {
      channelType,
      recipientsList,
      invalidRecipientsList,
      template,
      sender,
      loading,
      conversationId,
      submitted,
      errorMessage,
    });
  };

  return (
    <Container onDoubleClick={componentInfoLogger}>
      <NuacomComponent.Header {...props} center>
        <ChannelSelector
          onChange={handleChannelChange}
          activeChannelType={channelType}
        />
        <Right>
          <CancelButton
            onClick={() => navigate.conversations()}
            data-test-id="cancel-new-conversation">
            {t('screens.conversation.cancel')}
          </CancelButton>
        </Right>
      </NuacomComponent.Header>

      <Block className="parent-scrollable-container">
        <MessageHeader>
          <RecipientsSelector
            sender={sender}
            template={template}
            initialValues={initialRecipientsList}
            onChange={setRecipientsList}
            onValidationFailure={setInvalidRecipientsList}
            maxItems={MAX_RECIPIENTS}
          />
          <SenderSelector
            sender={sender}
            defaultSenders={template?.senderNames || []}
            onChange={value => setSender(value)}
          />
        </MessageHeader>
        <ConversationWarnings
          invalidRecipients={invalidRecipientsList}
          recipients={recipientsList}
          template={template}
          maxRecipients={MAX_RECIPIENTS}
        />
        <MessageContent>
          <MessageTextInput
            onSend={handleSendMessage}
            channelType={channelType as ChannelTypeEnum}
            channelOptions={[channelType]}
            onChannelChange={channel => setChannelType(channel)}
            onTemplateAppend={handleOnTemplateAppend}
            isLoading={loading}
            disabled={isSendMessageBtnDisabled}
            hasNoReceipientsOrSender={hasNoReceipientsOrSender}
            onTemplateRemoved={handleOnTemplateRemoved}
            indicateTemplateRequired
          />
        </MessageContent>
      </Block>
    </Container>
  );
}
